import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "./Reveal";
import { useLang, SERVICES } from "../lib/i18n";
import { scrollToId } from "./Nav";
import { slugForService } from "../lib/treatments";

const chip = "text-[0.72rem] tracking-[0.08em] px-3 py-1.5 rounded-full border border-[#F6F2EC]/30 bg-[#14110F]/30 backdrop-blur-md";

const Chip = ({ name }) => {
  const slug = slugForService(name);
  if (!slug) return <li className={chip}>{name}</li>;
  return (
    <li>
      <Link to={`/trattamenti/${slug}`} data-testid={`service-page-link-${slug}`} className={`${chip} inline-flex items-center gap-1 border-[#C9A66B]/70 transition-colors duration-300 hover:bg-[#C9A66B] hover:text-[#14110F]`}>
        {name} <ArrowUpRight className="w-3 h-3" />
      </Link>
    </li>
  );
};

const LAYOUT = {
  face: "lg:col-span-4 lg:row-span-2",
  body: "lg:col-span-2 lg:row-span-2",
  hands: "lg:col-span-3",
  hair: "lg:col-span-3",
};

const Card = ({ s, i, lang, book, onPick }) => {
  const c = s[lang];
  const big = s.key === "face";
  return (
    <motion.article
      data-testid={`service-card-${s.key}`}
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 1, delay: (i % 3) * 0.1, ease: [0.22, 1, 0.36, 1] }}
      className={`group relative overflow-hidden bg-[#14110F] h-[380px] sm:h-[420px] lg:h-auto ${LAYOUT[s.key]}`}
    >
      <img src={s.img} alt={c.t} className={`absolute inset-0 w-full h-full object-cover ${s.pos} opacity-75 scale-[1.02] transition-[transform,opacity] duration-[1400ms] ease-out group-hover:scale-110 group-hover:opacity-55`} />
      <div className="absolute inset-0 bg-gradient-to-t from-[#14110F] via-[#14110F]/45 to-transparent" />
      <div className="relative h-full flex flex-col justify-between p-6 sm:p-8 text-[#F6F2EC]">
        <div className="flex items-start justify-between">
          <span className="font-stencil text-sm tracking-[0.3em] text-[#C9A66B]">0{i + 1}</span>
          <button
            data-testid={`service-book-${s.key}`}
            onClick={() => onPick(c.s[0])}
            aria-label={`${book} ${c.t}`}
            className="w-11 h-11 rounded-full border border-[#F6F2EC]/40 flex items-center justify-center transition-[background-color,transform,border-color] duration-500 group-hover:bg-[#C9A66B] group-hover:border-[#C9A66B] group-hover:text-[#14110F] group-hover:rotate-45"
          >
            <ArrowUpRight className="w-4 h-4" />
          </button>
        </div>
        <div>
          <h3 className={`font-serif-d font-light leading-none ${big ? "text-5xl sm:text-7xl" : "text-4xl sm:text-[2.6rem]"}`}>{c.t}</h3>
          <ul className="mt-5 flex flex-wrap gap-2">
            {c.s.map((x) => <Chip key={x} name={x} />)}
          </ul>
        </div>
      </div>
    </motion.article>
  );
};

export const Services = ({ onPick }) => {
  const { t, lang } = useLang();
  const pick = (name) => { onPick(name); scrollToId("booking"); };
  return (
    <section id="services" data-testid="services-section" className="bg-[#ECE5DA] py-24 sm:py-36">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-10">
        <div className="grid lg:grid-cols-12 gap-8 mb-14 sm:mb-20 items-end">
          <div className="lg:col-span-7">
            <Reveal><p className="overline text-[#8A6A3A] mb-6">{t.services.over}</p></Reveal>
            <Reveal delay={0.05}><h2 className="font-serif-d font-light text-[2.6rem] sm:text-7xl leading-[0.95] text-[#14110F]" data-testid="services-title">{t.services.title}</h2></Reveal>
          </div>
          <Reveal delay={0.1} className="lg:col-span-4 lg:col-start-9">
            <p className="text-[#4f4841] font-light leading-relaxed">{t.services.sub}</p>
            <Link to="/quiz-pelle" data-testid="services-quiz-link" className="group mt-6 inline-flex items-center gap-2 text-[0.72rem] tracking-[0.2em] uppercase text-[#14110F] underline underline-offset-8 decoration-[#8A6A3A]">
              {t.services.quiz} <ArrowUpRight className="w-4 h-4 transition-transform duration-500 group-hover:rotate-45" />
            </Link>
          </Reveal>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 lg:auto-rows-[300px] gap-3 sm:gap-4">
          {SERVICES.map((s, i) => (
            <Card key={s.key} s={s} i={i} lang={lang} book={t.services.book} onPick={pick} />
          ))}
        </div>
      </div>
    </section>
  );
};
